import React, { Fragment } from "react";
import { Form, Header, Label } from "semantic-ui-react";

import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSchemaTranslation } from "../../hooks/useSchemaTranslation";
import { DateTimePicker, PickerUtils } from "../DateTimePicker";

const getTextProps = (t: TFunction) => ({
  starts: t("Alkaa"),
  ends: t("Päättyy")
});

export type TValue = {
  start?: string,
  end?: string;
};

interface DateRangePickerProps {
  format?: "date" | "time" | "date-time",
  valueFormat?: "date" | "time" | "date-time" | "any",

  value?: TValue;
  onChange: (value?: TValue) => any;

  minDate?: string | true;
  maxDate?: string | true;
  hourRoundingValue?: number;
  pastDateWarning?: boolean;

  start?: { title?: string; errors?: any[]; required?: boolean; },
  end?: { title?: string; errors?: any[]; required?: boolean; }

  disabled?: boolean;
  clearable?: boolean;
  autoSelectTime?: boolean;
  inline?: boolean;
}

const DateRangeWidget: React.FC<DateRangePickerProps> = ({
  disabled,
  inline = false,
  value: uknownValue,
  onChange: uknownOnChange,
  format = "date",

  hourRoundingValue = 1,
  pastDateWarning = true,
  autoSelectTime = true,
  minDate,
  maxDate,

  start: startsOptions,
  end: endsOptions,
}) => {
  const t = useSchemaTranslation().t
  const texts = getTextProps(useTranslation().t)

  const value: TValue | undefined = uknownValue;

  const onChange = (value?: TValue) => { uknownOnChange(value); };

  const getValue = (key: keyof TValue) => value && value[key] ? value[key] : undefined;
  const getMaxMinOptions = (opt: string | true | undefined) => opt === true ? new Date().toISOString() : opt

  const starts = getValue("start") as string | undefined;
  const ends = getValue("end") as string | undefined;;

  const defaultRangeOptions = {
    start: {
      title: startsOptions?.title || "Alkaa",
      errors: startsOptions?.errors,
      required: startsOptions?.required ?? true
    },
    end: {
      title: endsOptions?.title || "Päättyy",
      errors: endsOptions?.errors,
      required: endsOptions?.required ?? true
    }
  };

  const updateStarts = (newValue?: string) => {
    if (newValue !== value?.start) {
      onChange({ ...(value || {}), "start": newValue ? PickerUtils.ensureValueFormat(newValue, format) : undefined });
    }
  };

  const updateEnds = (newValue?: string) => {
    onChange({ ...(value || {}), "end": newValue ? PickerUtils.ensureValueFormat(newValue, format) : undefined });
  };

  const minStartsDate = getMaxMinOptions(minDate);
  const maxStartsDate = ends ? ends : getMaxMinOptions(maxDate);

  const minEndsDate = starts ? starts : minStartsDate;
  const maxEndsDate = getMaxMinOptions(maxDate)

  const renderPicker = (key: "start" | "end", text: string | null, children: any) => {
    const options = key == "start" ? defaultRangeOptions.start : defaultRangeOptions.end;
    const label = text || t(options?.title);
    const errors = options?.errors || [];

    return (
      <Form.Field error={errors.length} required={options?.required}>
        <Header className="Field-label" as="label"> {t(label)} </Header>
        {children}
        {errors.length ? <Label pointing negative prompt> {errors[0]}</Label> : null}
      </Form.Field>
    )
  };

  const Wrapper = ["time", "date"].includes(format) ? Form.Group : Fragment

  return (
    <Wrapper className="DateRangeWidget_wrapper" inline={inline}>

      {renderPicker("start", texts.starts, (
        <Fragment>
          <DateTimePicker
            //  key={"end" + [ends, starts].join("")}
            value={starts}
            onChange={updateStarts}
            minDate={minStartsDate}
            maxDate={maxStartsDate}
            format={format}
            pastDateWarning={pastDateWarning}
            disabled={disabled}
            autoSelectTime={autoSelectTime}
            required={defaultRangeOptions.start.required}
            hourRoundingValue={hourRoundingValue}
          />
        </Fragment>
      ))}

      {renderPicker("end", texts.ends, (
        <DateTimePicker
          // key={"start" + String(starts)}
          value={ends}
          onChange={updateEnds}
          minDate={minEndsDate}
          maxDate={maxEndsDate}
          format={format}
          disabled={disabled}
          autoSelectTime={autoSelectTime}
          hourRoundingValue={hourRoundingValue + 1}
          required={defaultRangeOptions.end.required}
        />
      ))}

    </Wrapper>
  );
}

DateRangeWidget.defaultProps = {
  disabled: false,
};

export default DateRangeWidget;
